import { useState, useEffect, useCallback, useMemo } from "react";
import { api } from "../App";
import { getApiPrefix } from "../utils/apiHelper";
import { Button } from "./ui/button";
import { Card, CardContent } from "./ui/card";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Bell } from "lucide-react";
import { CalendarGrid } from "./calendar/CalendarGrid";
import { DayEventsDialog } from "./calendar/DayEventsDialog";
import { ReminderFormDialog } from "./calendar/ReminderFormDialog";

const MONTHS = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

const EMPTY_FORM = { titolo: "", descrizione: "", data: "", ora: "", colore: "#6366f1", ripeti: "" };

export default function CalendarView() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents] = useState({ attivita: [], steps: [], promemoria: [] });
  const [reminderDialog, setReminderDialog] = useState({ open: false, reminder: null });
  const [dayEventsDialog, setDayEventsDialog] = useState({ open: false, date: null, events: [] });
  const [reminderForm, setReminderForm] = useState(EMPTY_FORM);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const getMonthRange = useCallback(() => {
    const start = new Date(year, month, 1).toISOString().split('T')[0];
    const end = new Date(year, month + 1, 0).toISOString().split('T')[0];
    return { start, end };
  }, [year, month]);

  const fetchEvents = useCallback(async () => {
    try {
      const { start, end } = getMonthRange();
      const res = await api.get(`${getApiPrefix()}/calendar-events?data_inizio=${start}&data_fine=${end}`);
      setEvents(res.data);
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') console.error("Error fetching calendar events:", err);
    }
  }, [getMonthRange]);

  useEffect(() => { fetchEvents(); }, [fetchEvents]);

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const startingDay = (firstDay.getDay() + 6) % 7;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const prevLast = new Date(year, month, 0).getDate();
    const days = [];
    for (let i = startingDay - 1; i >= 0; i--) days.push({ day: prevLast - i, isCurrentMonth: false, date: new Date(year, month - 1, prevLast - i) });
    for (let i = 1; i <= daysInMonth; i++) days.push({ day: i, isCurrentMonth: true, date: new Date(year, month, i) });
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) days.push({ day: i, isCurrentMonth: false, date: new Date(year, month + 1, i) });
    return days;
  }, [year, month]);

  const getEventsForDate = useCallback((date) => {
    const ds = date.toISOString().split('T')[0];
    const attivita = events.attivita.filter(e => e.data === ds);
    const steps = events.steps.filter(e => e.data === ds);
    const promemoria = events.promemoria.filter(e => e.data === ds);
    return { attivita, steps, promemoria, total: attivita.length + steps.length + promemoria.length };
  }, [events]);

  const openReminderDialog = (reminder = null, date = null) => {
    setReminderForm(reminder
      ? { titolo: reminder.titolo || "", descrizione: reminder.descrizione || "", data: reminder.data || "", ora: reminder.ora || "", colore: reminder.colore || "#6366f1", ripeti: reminder.ripeti || "" }
      : { ...EMPTY_FORM, data: date || new Date().toISOString().split('T')[0] }
    );
    setReminderDialog({ open: true, reminder });
  };

  const handleSaveReminder = async () => {
    if (!reminderForm.titolo.trim()) { toast.error("Il titolo è obbligatorio"); return; }
    if (!reminderForm.data) { toast.error("La data è obbligatoria"); return; }
    try {
      const payload = { ...reminderForm };
      if (!payload.ripeti) payload.ripeti = null;
      if (!payload.ora) payload.ora = null;
      if (reminderDialog.reminder) {
        await api.put(`${getApiPrefix()}/reminders/${reminderDialog.reminder.id}`, payload);
        toast.success("Promemoria aggiornato");
      } else {
        await api.post(`${getApiPrefix()}/reminders`, payload);
        toast.success("Promemoria creato");
      }
      setReminderDialog({ open: false, reminder: null });
      fetchEvents();
    } catch { toast.error("Errore salvataggio"); }
  };

  const handleDeleteReminder = async (id) => {
    try {
      await api.delete(`${getApiPrefix()}/reminders/${id}`);
      toast.success("Promemoria eliminato");
      setDayEventsDialog(prev => ({ ...prev, open: false }));
      fetchEvents();
    } catch { toast.error("Errore eliminazione"); }
  };

  const handleToggleComplete = async (reminder) => {
    try {
      await api.put(`${getApiPrefix()}/reminders/${reminder.id}`, { completato: !reminder.completato });
      fetchEvents();
    } catch { toast.error("Errore aggiornamento"); }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h2 className="text-xl font-bold">{MONTHS[month]} {year}</h2>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" onClick={() => setCurrentDate(new Date(year, month - 1, 1))}><ChevronLeft className="h-4 w-4" /></Button>
            <Button variant="outline" size="sm" onClick={() => setCurrentDate(new Date())}>Oggi</Button>
            <Button variant="outline" size="icon" onClick={() => setCurrentDate(new Date(year, month + 1, 1))}><ChevronRight className="h-4 w-4" /></Button>
          </div>
        </div>
        <Button onClick={() => openReminderDialog()} data-testid="add-reminder-btn">
          <Plus className="h-4 w-4 mr-2" /> Promemoria
        </Button>
      </div>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-4">
          <CalendarGrid calendarDays={calendarDays} getEventsForDate={getEventsForDate} onDayClick={(date) => setDayEventsDialog({ open: true, date, events: getEventsForDate(date) })} />
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-red-500" /><span>Priorità Alta</span></div>
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-yellow-500" /><span>Priorità Media</span></div>
        <div className="flex items-center gap-2"><div className="w-3 h-3 rounded bg-green-500" /><span>Priorità Bassa</span></div>
        <div className="flex items-center gap-2"><Bell className="h-3 w-3" /><span>Promemoria</span></div>
      </div>

      {/* Dialogs */}
      <DayEventsDialog dialog={dayEventsDialog} setDialog={setDayEventsDialog} onOpenReminder={openReminderDialog} onDeleteReminder={handleDeleteReminder} onToggleComplete={handleToggleComplete} />
      <ReminderFormDialog dialog={reminderDialog} setDialog={setReminderDialog} form={reminderForm} setForm={setReminderForm} onSave={handleSaveReminder} onDelete={handleDeleteReminder} />
    </div>
  );
}
